import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../hooks/use-auth";
import { useToast } from "../hooks/use-toast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const REGULAMENTO_URL = `${API}/regulamento`;

// ── Reusable field components ──────────────────────────────────────────
function ReadOnlyField({ label, value, className = "" }) {
  return (
    <div>
      <label className="text-sm font-medium text-stone-700 block mb-1">{label}</label>
      <p className={`text-sm text-stone-700 min-h-[2rem] flex items-center ${className}`}>
        {value || "\u2014"}
      </p>
    </div>
  );
}

function TextField({ label, value, onChange, placeholder, disabled, required, type = "text" }) {
  return (
    <div>
      <label className="text-sm font-medium text-stone-700 block mb-1">{label}</label>
      <input
        type={type}
        value={value || ""}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        className={`w-full h-10 px-3 rounded-md border border-stone-200 text-sm bg-white
          focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent
          ${disabled ? "bg-stone-100 text-stone-500 cursor-not-allowed" : ""}`}
      />
    </div>
  );
}

function SelectField({ label, value, onChange, options, disabled, placeholder = "Seleciona" }) {
  return (
    <div>
      <label className="text-sm font-medium text-stone-700 block mb-1">{label}</label>
      <select
        value={value || ""}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
        className={`w-full h-10 px-3 rounded-md border border-stone-200 text-sm bg-white
          focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent
          ${disabled ? "bg-stone-100 text-stone-400" : ""}`}
      >
        <option value="">{placeholder}</option>
        {options.map((opt, i) => (
          <option key={opt.id || i} value={opt.id}>{opt.descricao}</option>
        ))}
      </select>
    </div>
  );
}

function SectionHeader({ icon: Icon, children }) {
  return (
    <h2 className="text-lg font-bold text-stone-900 mb-4 flex items-center gap-2">
      {Icon && <Icon className="w-5 h-5 text-brand-600" />}
      {children}
    </h2>
  );
}

// ── Icons (inline SVG) ─────────────────────────────────────────────────
const UserIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-brand-600">
    <path d="M10 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM3.465 14.493a1.23 1.23 0 0 0 .41 1.412A9.957 9.957 0 0 0 10 18c2.31 0 4.438-.784 6.131-2.1.43-.333.604-.903.408-1.41a7.002 7.002 0 0 0-13.074.003Z" />
  </svg>
);

const ShirtIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-brand-600">
    <path d="M7.5 6.5v-2a1 1 0 011-1h7a1 1 0 011 1v2h4a1 1 0 011 1v2.5l-2.5 3-2-.5V21h-10v-9.5l-2 .5-2.5-3V7.5a1 1 0 011-1h4z" />
  </svg>
);

const MoneyIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-brand-600">
    <path d="M12 7.5a2.25 2.25 0 100 4.5 2.25 2.25 0 000-4.5z" />
    <path fillRule="evenodd" d="M1.5 4.875C1.5 3.839 2.34 3 3.375 3h17.25c1.035 0 1.875.84 1.875 1.875v9.75c0 1.035-.84 1.875-1.875 1.875H3.375A1.875 1.875 0 011.5 14.625v-9.75zM8.25 9.75a3.75 3.75 0 117.5 0 3.75 3.75 0 01-7.5 0zM18.75 9a.75.75 0 00-.75.75v.008c0 .414.336.75.75.75h.008a.75.75 0 00.75-.75V9.75a.75.75 0 00-.75-.75h-.008zM4.5 9.75A.75.75 0 015.25 9h.008a.75.75 0 01.75.75v.008a.75.75 0 01-.75.75H5.25a.75.75 0 01-.75-.75V9.75z" clipRule="evenodd" />
  </svg>
);

const ArrowLeftIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
    <path fillRule="evenodd" d="M17 10a.75.75 0 01-.75.75H5.612l4.158 3.96a.75.75 0 11-1.04 1.08l-5.5-5.25a.75.75 0 010-1.08l5.5-5.25a.75.75 0 111.04 1.08L5.612 9.25H16.25A.75.75 0 0117 10z" clipRule="evenodd" />
  </svg>
);

// ═════════════════════════════════════════════════════════════════════════
export default function RenovacaoPage() {
  const { token, athlete, refreshProfile } = useAuth();
  const { toast } = useToast();

  // ── State ────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [error, setError] = useState(null);

  // Form data from API
  const [season, setSeason] = useState("");
  const [deadline, setDeadline] = useState("");
  const [canRenew, setCanRenew] = useState(true);
  const [blockedReason, setBlockedReason] = useState("");
  const [isAtleta, setIsAtleta] = useState(true);
  const [isReinscrito, setIsReinscrito] = useState(false);

  // Reference data
  const [modalidades, setModalidades] = useState([]);
  const [escaloes, setEscaloes] = useState([]);
  const [generos, setGeneros] = useState([]);
  const [documentosIdentificacao, setDocumentosIdentificacao] = useState([]);
  const [escaloesEquipamentos, setEscaloesEquipamentos] = useState([]);
  const [escalaoPrecos, setEscalaoPrecos] = useState([]);
  const [escalaoSugerido, setEscalaoSugerido] = useState("");

  // Form fields
  const [form, setForm] = useState({
    name: "",
    gender: "",
    phone: "",
    address: "",
    modality: "",
    equipamento_tamanho: "",
    documento_identificacao: "",
    numero_identificacao: "",
    requer_equipamento: false,
    terms_accepted: false,
    confirmed: false,
    insurance_accepted: false,
  });

  // Read-only fields
  const [nif, setNif] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [nacionalidade, setNacionalidade] = useState("");
  const [escalao, setEscalao] = useState("");
  const [dataInscricao, setDataInscricao] = useState("");

  // Prices
  const [prices, setPrices] = useState({ seguro: "0.00", mensalidade: "0.00", anuidade: "", reinscricao: "", total: "0.00" });

  // Tipo de pagamento (mensal/anual) da modalidade
  const [pagamento, setPagamento] = useState("mensal");
  const [pagamentoAnualDisponivel, setPagamentoAnualDisponivel] = useState(true);
  const [pagamentoModalidadeDisponivel, setPagamentoModalidadeDisponivel] = useState(true);

  // ── Load form data ───────────────────────────────────────────────────
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/athletes/me/renewal-form`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const d = res.data;
        setSeason(d.season);
        setDeadline(d.renewal_deadline);
        setCanRenew(d.can_renew);
        setBlockedReason(d.blocked_reason || "");
        setIsAtleta(d.is_atleta);
        setIsReinscrito(d.is_reinscrito);

        const a = d.athlete || {};
        setNif(a.nif || "");
        setBirthDate(a.birth_date || "");
        setNacionalidade(a.nacionalidade || "");
        setEscalao(d.escalao_sugerido || a.escalao || "");
        setEscalaoSugerido(d.escalao_sugerido || "");
        setDataInscricao(a.data_socio || "");

        setModalidades(d.modalidades || []);
        setEscaloes(d.escaloes || []);
        setGeneros(d.generos || []);
        setDocumentosIdentificacao(d.documentos_identificacao || []);
        setEscaloesEquipamentos(d.escaloes_equipamentos || []);
        setEscalaoPrecos(d.escalao_precos || []);
        setPrices(d.prices || { seguro: "0.00", mensalidade: "0.00", anuidade: "", reinscricao: "", total: "0.00" });
        const anualDisponivel = d.pagamento_anual_disponivel !== false;
        setPagamentoAnualDisponivel(anualDisponivel);
        setPagamentoModalidadeDisponivel(d.pagamento_modalidade_disponivel !== false);
        setPagamento(anualDisponivel && d.pagamento === "anual" ? "anual" : "mensal");

        setForm(prev => ({
          ...prev,
          name: a.name || "",
          gender: a.gender || "",
          phone: a.phone || "",
          address: a.address || "",
          modality: a.modality || "",
          documento_identificacao: a.documento_identificacao || "",
          numero_identificacao: a.numero_identificacao || "",
        }));
      } catch (err) {
        const msg = err?.response?.data?.detail || "Erro ao carregar formulário de renovação.";
        setError(msg);
        toast({ title: "Erro", description: msg, variant: "destructive" });
      } finally {
        setLoading(false);
      }
    })();
  }, [token, toast]);

  // ── Handlers ─────────────────────────────────────────────────────────
  const updateField = useCallback((field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.terms_accepted) {
      toast({ title: "Atenção", description: "Precisas de aceitar os termos para renovar.", variant: "destructive" });
      return;
    }
    if (!form.confirmed) {
      toast({ title: "Atenção", description: "Precisas de confirmar que os dados estão corretos.", variant: "destructive" });
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/athletes/me/renew`, {
        name: form.name,
        gender: form.gender,
        phone: form.phone,
        address: form.address,
        modality: form.modality,
        equipamento_tamanho: form.requer_equipamento ? (form.equipamento_tamanho || "") : "",
        documento_identificacao: form.documento_identificacao,
        numero_identificacao: form.numero_identificacao,
        terms_accepted: form.terms_accepted,
        confirmed: true,
        insurance_accepted: form.insurance_accepted,
        pagamento,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      await refreshProfile();
      setCompleted(true);
      toast({ title: "Inscrição renovada!", description: `Renovação para a época ${season} confirmada.` });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erro ao submeter renovação.";
      toast({ title: "Erro", description: msg, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  // ── Loading ──────────────────────────────────────────────────────────
  if (loading) {
    return (
      <main className="pt-32 pb-20 bg-stone-50 min-h-screen">
        <div className="max-w-4xl mx-auto px-6 flex items-center justify-center min-h-[40vh]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
        </div>
      </main>
    );
  }

  // ── Completed ────────────────────────────────────────────────────────
  if (completed) {
    return (
      <main className="pt-32 pb-20 bg-stone-50 min-h-screen">
        <div className="max-w-lg mx-auto px-6">
          <div className="bg-white rounded-2xl shadow-xl border border-stone-100 p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-stone-900 mb-2">Inscrição Renovada!</h1>
            <p className="text-stone-500 mb-8">
              A tua inscrição para a <strong>Época {season}</strong> foi confirmada com sucesso.
            </p>
            <Link
              to="/area-socio/dashboard"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-brand-600 to-brand-800
                hover:from-brand-700 hover:to-brand-900 text-white rounded-full py-3 px-8 text-base font-semibold"
            >
              Ir para o Dashboard
            </Link>
          </div>
        </div>
      </main>
    );
  }

  // ── Cannot renew ─────────────────────────────────────────────────────
  if (!canRenew) {
    const statusMsg = blockedReason
      ? blockedReason
      : isReinscrito
        ? "Já tens a inscrição renovada para a época atual."
        : "A renovação está disponível apenas para atletas.";

    return (
      <main className="pt-32 pb-20 bg-stone-50 min-h-screen">
        <div className="max-w-lg mx-auto px-6">
          <Link to="/area-socio/dashboard" className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-brand-700 transition-colors mb-8">
            <ArrowLeftIcon /> Voltar ao Dashboard
          </Link>
          <div className="bg-white rounded-2xl shadow-xl border border-stone-100 p-8 text-center">
            <h1 className="text-2xl font-bold text-stone-900 mb-4">Reinscrição indisponível</h1>
            <p className="text-stone-500">{statusMsg}</p>
          </div>
        </div>
      </main>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────
  if (error) {
    return (
      <main className="pt-32 pb-20 bg-stone-50 min-h-screen">
        <div className="max-w-lg mx-auto px-6">
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      </main>
    );
  }

  // ── Regular renewal form ─────────────────────────────────────────────
  return (
    <main className="pt-32 pb-20 bg-stone-50 min-h-screen">
      <div className="max-w-lg mx-auto px-6">
        <Link to="/area-socio/dashboard" className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-brand-700 transition-colors mb-8">
          <ArrowLeftIcon /> Voltar ao Dashboard
        </Link>

        <div className="bg-white rounded-2xl shadow-xl border border-stone-100 p-8 mb-6">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-stone-900">Renovar Inscrição</h1>
            <p className="text-stone-500 text-sm mt-1">
              Verifica e atualiza os teus dados para a <strong>Época {season}</strong>.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* ── DADOS DO SÓCIO ───────────────────────────────────── */}
            <fieldset className="border border-stone-200 rounded-xl p-5">
              <legend className="text-sm font-bold text-brand-700 px-2 uppercase tracking-wider">
                Dados do Sócio
              </legend>

              <div className="space-y-4 mt-1">
                {/* NIF (read-only) */}
                <ReadOnlyField label="NIF" value={nif} />
                <p className="text-xs text-stone-400 -mt-2">O NIF não pode ser alterado.</p>

                {/* Nome */}
                <TextField
                  label="Nome completo *"
                  value={form.name}
                  onChange={v => updateField("name", v)}
                  required
                />

                {/* Row: Data nasc + Género + Telefone */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <ReadOnlyField label="Data de nascimento" value={birthDate} />
                  <SelectField
                    label="Género"
                    value={form.gender}
                    onChange={v => updateField("gender", v)}
                    options={generos}
                    placeholder="Seleciona"
                  />
                  <TextField
                    label="Telefone"
                    value={form.phone}
                    onChange={v => updateField("phone", v)}
                    placeholder="912 345 678"
                  />
                </div>

                {/* Morada */}
                <TextField
                  label="Morada"
                  value={form.address}
                  onChange={v => updateField("address", v)}
                  placeholder="Rua, nº, andar"
                />

                {/* Row: Modalidade + Escalão + Doc. Identificação */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <SelectField
                    label="Modalidade"
                    value={form.modality}
                    onChange={v => updateField("modality", v)}
                    options={modalidades}
                    placeholder="Seleciona"
                  />
                  <ReadOnlyField label="Escalão" value={escalao} />
                  <SelectField
                    label="Doc. Identificação"
                    value={form.documento_identificacao}
                    onChange={v => updateField("documento_identificacao", v)}
                    options={documentosIdentificacao}
                    placeholder="Seleciona"
                  />
                </div>

                {/* Row: N. Identificação + Nacionalidade */}
                <div className="grid grid-cols-2 gap-3">
                  <TextField
                    label="N. Identificação"
                    value={form.numero_identificacao}
                    onChange={v => updateField("numero_identificacao", v)}
                    placeholder="Número do documento"
                  />
                  <ReadOnlyField label="Nacionalidade" value={nacionalidade} />
                </div>
              </div>
            </fieldset>

            {/* ── EQUIPAMENTO ──────────────────────────────────────── */}
            <div className="bg-white rounded-2xl shadow-xl border border-stone-100 p-6">
              <h3 className="text-sm font-bold text-brand-700 mb-3">Equipamento</h3>

              <div className="flex items-center gap-2 mb-3">
                <input
                  type="checkbox"
                  id="req-equip"
                  checked={form.requer_equipamento}
                  onChange={e => updateField("requer_equipamento", e.target.checked)}
                  className="w-4 h-4 rounded border-stone-300 text-brand-600 focus:ring-brand-500"
                />
                <label htmlFor="req-equip" className="text-sm cursor-pointer">
                  Solicitar equipamento
                </label>
              </div>

              <div>
                <label className="text-sm font-medium text-stone-700 block mb-1">Tamanho</label>
                <select
                  value={form.equipamento_tamanho || ""}
                  disabled={!form.requer_equipamento}
                  onChange={e => updateField("equipamento_tamanho", e.target.value)}
                  className={`w-full h-10 px-3 rounded-md border border-stone-200 text-sm bg-white
                    focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent
                    ${!form.requer_equipamento ? "bg-stone-100 text-stone-400" : ""}`}
                >
                  <option value="">Seleciona...</option>
                  {escaloesEquipamentos.map((eq, i) => (
                    <option key={eq.id || i} value={eq.id}>{eq.descricao}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* ── PAGAMENTO ──────────────────────────────────────── */}
            {pagamentoModalidadeDisponivel && (
              <fieldset className="border border-stone-200 rounded-xl p-5">
                <legend className="text-sm font-bold text-brand-700 px-2 uppercase tracking-wider">
                  Pagamento
                </legend>
                <div className="mt-1">
                  <label className="text-sm font-medium text-stone-700 block mb-2">Pagamento da modalidade</label>
                  <div className="flex gap-4">
                    {(["mensal", "anual"].filter(p => p !== "anual" || pagamentoAnualDisponivel)).map(p => (
                      <label key={p} className={`flex-1 cursor-pointer rounded-xl border-2 p-3 text-center transition-all
                        ${pagamento === p ? "border-brand-500 bg-brand-50" : "border-stone-200 hover:border-stone-300"}`}>
                        <input type="radio" name="pagamento" value={p} checked={pagamento === p}
                          onChange={() => setPagamento(p)} className="sr-only" />
                        <p className="font-semibold text-stone-900 text-sm">{p === "mensal" ? "Mensal" : "Anual"}</p>
                      </label>
                    ))}
                  </div>
                  {!pagamentoAnualDisponivel && (
                    <p className="text-xs text-stone-500 mt-2">
                      O pagamento anual só está disponível até ao fim de setembro. A partir de outubro apenas é possível o pagamento mensal.
                    </p>
                  )}
                </div>
              </fieldset>
            )}

            {/* ── VALORES DA REINSCRIÇÃO ──────────────────────────── */}
            <fieldset className="border border-stone-200 rounded-xl p-5">
              <legend className="text-sm font-bold text-brand-700 px-2 uppercase tracking-wider">
                Valores da Reinscrição
              </legend>
              <div className="grid grid-cols-2 gap-4 mt-1">
                <ReadOnlyField label="Seguro" value={`${prices.seguro}€`} />
                {pagamentoModalidadeDisponivel && (pagamento === "anual"
                  ? <ReadOnlyField label="Anuidade (modalidade)" value={prices.anuidade ? `${prices.anuidade}€` : "—"} />
                  : <ReadOnlyField label="Mensalidade" value={`${prices.mensalidade}€`} />)}
                <ReadOnlyField label="Reinscrição" value={prices.reinscricao ? `${prices.reinscricao}€` : "—"} />
              </div>
            </fieldset>

            {/* ── TERMS ───────────────────────────────────────────── */}
            <div className="space-y-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.terms_accepted}
                  onChange={e => updateField("terms_accepted", e.target.checked)}
                  className="w-4 h-4 mt-1 rounded border-stone-300 text-brand-600 focus:ring-brand-500"
                />
                <span className="text-sm text-stone-600">
                  Aceito os valores apresentados e confirmo que os meus dados estão corretos.
                  Li e aceito o{" "}
                  <a href={REGULAMENTO_URL} target="_blank" rel="noreferrer" className="underline text-brand-700">
                    Regulamento de Atletismo
                  </a>{" "}
                  interno do CATV para a <strong>Época {season}</strong>.
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.confirmed}
                  onChange={e => updateField("confirmed", e.target.checked)}
                  className="w-4 h-4 mt-1 rounded border-stone-300 text-brand-600 focus:ring-brand-500"
                />
                <span className="text-sm text-stone-600">
                  Confirmo que todos os dados apresentados estão corretos e autorizo a renovação da inscrição.
                </span>
              </label>
            </div>

            {/* ── SUBMIT ──────────────────────────────────────────── */}
            <button
              type="submit"
              disabled={submitting || !form.terms_accepted || !form.confirmed}
              className="w-full bg-gradient-to-r from-ember-400 to-ember-600 hover:from-ember-500 hover:to-ember-700
                disabled:opacity-60 text-white rounded-full py-6 text-base font-semibold flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>A renovar...</>
              ) : (
                <>Renovar Inscrição — Época {season}</>
              )}
            </button>
          </form>
        </div>
      </div>
    </main>
  );
}
